/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @nx/enforce-module-boundaries */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormArray,
  UntypedFormControl,
} from '@angular/forms';
import { CorrectiveMaintenanceApiClient } from '@ellf/app-api-client';
import { AttachmentDto, DocumentDto } from '@ellf/common-dto';
import { isEqual } from 'lodash';
import { v4 } from 'uuid';

import moment from 'moment';

export interface DocumentType {
  id: string;
  code: string;
  description: string;
}

export interface Document {
  id?: string;
  documentType: DocumentType;
  attachment: AttachmentDto[];
  notes: string;
  createdDate?: string;
  synchWithCoswin?: string | null | undefined;
  toSync?: boolean;
}

@Component({
    selector: 'ellf-document-type-reader',
    templateUrl: './document-type-reader.component.html',
    styleUrls: ['./document-type-reader.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DocumentTypeReaderComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DocumentTypeReaderComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class DocumentTypeReaderComponent
  implements OnDestroy, ControlValueAccessor, OnInit
{
  @Input()
  accept = '*';

  @Input()
  saved = false;

  @Input()
  btnSync = false;

  private _readOnly = false;

  @Input()
  set readOnly(value: boolean) {
    this._readOnly = value;
    if (value) {
      this.mandatoryFormArray.disable({ emitEvent: false });
      this.additionalFormArray.disable({ emitEvent: false });
    } else if (!this.isDisabled) {
      this.mandatoryFormArray.enable({ emitEvent: false });
      this.additionalFormArray.enable({ emitEvent: false });
    }
    this.cd.detectChanges();
  }

  get readOnly() {
    return this._readOnly;
  }

  @Input()
  documentTypes: Array<DocumentType> = [];

  private _requiredDocumentTypes: Array<DocumentType> = [];
  get requiredDocumentTypes() {
    return this._requiredDocumentTypes;
  }

  @Input()
  set requiredDocumentTypes(dts: Array<DocumentType>) {
    if (isEqual(dts, this._requiredDocumentTypes)) {
      return;
    }

    this._requiredDocumentTypes = dts;

    this.mandatoryFormArray.clear({ emitEvent: false });

    this._requiredDocumentTypes.forEach((dt) => {
      const fg = new FormGroup({
        documentType: new UntypedFormControl(dt),
        attachment: new UntypedFormControl(null),
        id: new UntypedFormControl(''),
        notes: new UntypedFormControl(''),
        // createdDate: new UntypedFormControl(''),
      });
      if (this.isDisabled || this._readOnly) {
        fg.disable({ emitEvent: false });
      } else {
        fg.enable({ emitEvent: false });
      }
      this.mandatoryFormArray?.push(fg, { emitEvent: false });
    });
  }

  value?: Document[] = [];

  isValid = true;

  isDisabled = false;

  mandatoryFormArray: UntypedFormArray = new UntypedFormArray([]);

  additionalFormArray: UntypedFormArray = new UntypedFormArray([]);

  constructor(
    private cd: ChangeDetectorRef,
    private correctiveMaintenanceService: CorrectiveMaintenanceApiClient
  ) {}

  propagateChange = (file?: Document[]) => {};
  onTouched = () => {};
  validateFn: any = () => {};

  writeValue(value?: DocumentDto[]) {
    const docs = value ? [...value] : [];

    this.mandatoryFormArray.controls.forEach((formGroup) => {
      const documentTypeControl = formGroup.get('documentType') as FormGroup;
      const documentTypeId = documentTypeControl.value.id;

      const newDocIndex = docs.findIndex(
        (v) => v.documentType?.id === documentTypeId
      );
      if (newDocIndex >= 0) {
        formGroup.patchValue(
          {
            attachment: docs[newDocIndex].attachment,
            id: docs[newDocIndex].id,
            notes: docs[newDocIndex].notes,
            createdDate: moment(docs[newDocIndex].createdDate).format(
              'DD/MM/YYYY HH:mm'
            ),
            synchWithCoswin: docs[newDocIndex].synchWithCoswin,
            toSync: docs[newDocIndex].synchWithCoswin === null,
          },
          { emitEvent: false }
        );

        docs.splice(newDocIndex, 1);
      }
    });

    this.additionalFormArray.clear({ emitEvent: false });
    docs.forEach((doc) => {
      const prevDocs = this.additionalFormArray.value as Document[];
      const prevDocIndex = prevDocs.findIndex((d) => d.id === doc.id);
      if (prevDocIndex >= 0) {
        this.additionalFormArray.at(prevDocIndex).patchValue(
          {
            documentType: doc.documentType,
            attachment: doc.attachment,
            notes: doc.notes,
            createdDate: moment(doc.createdDate).format('DD/MM/YYYY HH:mm'),
            synchWithCoswin: doc.synchWithCoswin,
            toSync: doc.synchWithCoswin === null,
          },
          { emitEvent: false }
        );
      } else {
        const fg = new FormGroup({
          documentType: new UntypedFormControl(doc.documentType),
          attachment: new UntypedFormControl(doc.attachment),
          id: new UntypedFormControl(doc.id),
          notes: new UntypedFormControl(doc.notes),
          createdDate: new UntypedFormControl(
            moment(doc.createdDate).format('DD/MM/YYYY HH:mm')
          ),
          synchWithCoswin: new UntypedFormControl(doc.synchWithCoswin),
          toSync: new UntypedFormControl(doc.synchWithCoswin === null),
        });
        if (this.isDisabled) {
          fg.disable({ emitEvent: false });
        } else {
          fg.enable({ emitEvent: false });
        }
        this.additionalFormArray.push(fg, { emitEvent: false });
      }
    });

    this.cd.detectChanges();
  }

  registerOnChange(fn: (value?: Document[]) => void) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean) {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.mandatoryFormArray.disable({ emitEvent: false });
      // this.mandatoryFormArray.controls.forEach((c) =>
      //   c.disable({ emitEvent: false })
      // );
      this.additionalFormArray.disable({ emitEvent: false });
      // this.additionalFormArray.controls.forEach((c) =>
      //   c.disable({ emitEvent: false })
      // );
    } else if (!this._readOnly) {
      this.mandatoryFormArray.enable({ emitEvent: false });
      // this.mandatoryFormArray.controls.forEach((c) =>
      //   c.enable({ emitEvent: false })
      // );
      this.additionalFormArray.enable({ emitEvent: false });
      // this.additionalFormArray.controls.forEach((c) =>
      //   c.enable({ emitEvent: false })
      // );
    }
    this.cd.detectChanges();
  }

  removeAt(i: number) {
    if (confirm("Sei sicuro di voler eliminare l'elemento?")) {
      this.additionalFormArray.removeAt(i);
    }
  }

  validate(control: UntypedFormControl) {
    const res = this.validateFn(control);
    this.isValid = res == null;
    return res;
  }

  ngOnInit(): void {
    this.mandatoryFormArray.valueChanges.subscribe(() => {
      this.onChanged();
    });
    this.additionalFormArray.valueChanges.subscribe(() => {
      this.onChanged();
    });
  }

  private onChanged() {
    const mandatoryDocuments = this.mandatoryFormArray.value as Document[];
    const additionalDocuments = this.additionalFormArray.value as Document[];

    this.propagateChange([
      ...mandatoryDocuments.filter((d) => d.attachment?.length),
      ...additionalDocuments.filter((d) => d.attachment?.length),
    ]);
    this.onTouched();
  }

  getDocumentTypeDescription(i: number) {
    const documentType = this.mandatoryFormArray.at(i)?.get('documentType')
      ?.value as DocumentType;
    return documentType.description;
  }

  ngOnDestroy() {
    this.cd.detach();
  }

  addDocument() {
    this.additionalFormArray.insert(
      0,
      new FormGroup({
        id: new UntypedFormControl(v4()),
        documentType: new UntypedFormControl(),
        attachment: new UntypedFormControl(),
        notes: new UntypedFormControl(),
        createdDate: new UntypedFormControl(),
        synchWithCoswin: new UntypedFormControl(undefined),
        toSync: new UntypedFormControl(false),
      })
    );
    // this.additionalFormArray.push(
    //   new FormGroup({
    //     id: new UntypedFormControl(v4()),
    //     documentType: new UntypedFormControl(),
    //     attachment: new UntypedFormControl(),
    //     notes: new UntypedFormControl(),
    //     createdDate: new UntypedFormControl(),
    //   })
    // );
  }
  synchDocument(documentId: string) {
    this.correctiveMaintenanceService
      .synchDocumentWithGlobalService(documentId)
      .subscribe(() => {
        window.location.reload();
      });
  }
}
